/**
 * Atlas PM · 项目类型 · 新建 / 编辑弹窗（P4.5 / P6.35）
 *
 * - 新建：code 不再由用户填写，提交时不含 code，由服务端自动生成唯一值
 * - 编辑：code 只读展示（后端 PATCH 亦会忽略 code）
 * - 后端错误 message 原样展示，不吞掉
 */

import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import { ApiError } from '../../../api/client';
import { type ProjectTypeItem } from './api';
import { COLOR_PALETTE, defaultColor } from '../../../components/color-palette';
import { useCreateProjectType, useUpdateProjectType } from './useProjectTypes';
import ColorField from '../../../components/ColorField';

export interface ProjectTypeFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** 编辑态必传 */
  item?: ProjectTypeItem | null;
  onClose(): void;
  onSaved(item: ProjectTypeItem, mode: 'create' | 'edit'): void;
}

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export default function ProjectTypeFormDialog({
  open,
  mode,
  item,
  onClose,
  onSaved,
}: ProjectTypeFormDialogProps) {
  const isEdit = mode === 'edit';

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createMut = useCreateProjectType();
  const updateMut = useUpdateProjectType();
  const mutation = isEdit ? updateMut : createMut;
  const busy = mutation.isPending;

  const itemId = item?.id;

  useEffect(() => {
    if (!open) return;
    setNameErr(null);
    setError(null);
    createMut.reset();
    updateMut.reset();
    if (isEdit && item) {
      setCode(item.code);
      setName(item.name);
      setColor(item.color);
      setSortOrder(String(item.sortOrder));
      setIsActive(item.isActive);
    } else {
      setCode('');
      setName('');
      setColor(defaultColor());
      setSortOrder('0');
      setIsActive(true);
    }
    // 仅在弹窗打开 / 切换对象时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, itemId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = () => {
    const n = name.trim();
    if (!n) {
      setNameErr('名称不能为空');
      return;
    }

    const parsed = Number(sortOrder);
    const safeSort = Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;

    setError(null);
    const onSuccess = (saved: ProjectTypeItem) => onSaved(saved, mode);
    const onError = (err: unknown) => setError(errorText(err, '保存失败'));

    if (isEdit) {
      if (!item) return;
      updateMut.mutate(
        { id: item.id, input: { name: n, color, sortOrder: safeSort, isActive } },
        { onSuccess, onError },
      );
    } else {
      // ⭐ P6.35：不传 code，由服务端自动生成唯一值
      createMut.mutate({ name: n, color, sortOrder: safeSort, isActive }, { onSuccess, onError });
    }
  };

  return (
    <div className="pt-modal-overlay" onClick={onClose}>
      <div
        className="pt-modal"
        role="dialog"
        aria-modal="true"
        data-testid="pt-form"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-modal__head">
          <span className="pt-modal__title">{isEdit ? '编辑项目类型' : '新建项目类型'}</span>
          <button
            type="button"
            className="pt-modal__close"
            aria-label="关闭"
            data-testid="pt-form-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <form
          className="pt-modal__body"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="pt-field">
            <label className="pt-field__label" htmlFor={isEdit ? 'pt-code' : undefined}>
              代码
            </label>
            {isEdit ? (
              <>
                <input
                  id="pt-code"
                  className="pt-input"
                  data-testid="pt-code"
                  value={code}
                  readOnly
                  disabled
                />
                <div className="pt-field__hint">代码是身份标识，创建后不可修改</div>
              </>
            ) : (
              <div className="pt-field__hint" data-testid="pt-code-note">
                代码由系统自动生成
              </div>
            )}
          </div>

          <div className="pt-field">
            <label className="pt-field__label" htmlFor="pt-name">
              名称<em className="pt-field__req">*</em>
            </label>
            <input
              id="pt-name"
              className="pt-input"
              data-testid="pt-name"
              placeholder="如 充电站"
              autoComplete="off"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameErr(null);
              }}
            />
            {nameErr && <div className="pt-field__err">{nameErr}</div>}
          </div>

          <div className="pt-field">
            <label className="pt-field__label">颜色</label>
            <ColorField
              value={color}
              onChange={setColor}
              palette={COLOR_PALETTE}
              testIdPrefix="pt"
            />
          </div>

          <div className="pt-row">
            <div className="pt-field pt-field--grow">
              <label className="pt-field__label" htmlFor="pt-sort">
                排序
              </label>
              <input
                id="pt-sort"
                type="number"
                min={0}
                className="pt-input"
                data-testid="pt-sort"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>

            <label className="pt-check">
              <input
                type="checkbox"
                data-testid="pt-active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              启用
            </label>
          </div>

          {error && (
            <div className="pt-form-error" data-testid="pt-form-error">
              ⚠ {error}
            </div>
          )}

          <div className="pt-modal__foot">
            <Button
              type="button"
              variant="ghost"
              data-testid="pt-cancel"
              onClick={onClose}
              disabled={busy}
            >
              取消
            </Button>
            <Button type="submit" variant="primary" data-testid="pt-submit" disabled={busy}>
              {busy ? '提交中…' : isEdit ? '保存修改' : '创建'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
